import { useState } from "react";
import { api, stageMeta } from "@/lib/api";
import Modal from "@/components/Modal";
import { toast } from "sonner";

const CARRIERS = ["DHL", "Fedex", "Bluedart", "Aramex", "Airwings"];

/**
 * Shown whenever a deal's stage changes. Captures an optional remark (logged to
 * the deal's activity trail) and, when moving to "Sample Sent", the details of
 * the shipment to create and link to the deal.
 *
 * Props:
 *   deal      — the deal being moved (needs id, customer_id, customer_name)
 *   fromStage — current stage key
 *   toStage   — target stage key
 *   onCancel  — called when the user aborts (revert any optimistic UI)
 *   onDone    — called after the stage change (and shipment) are persisted
 */
export default function StageChangeModal({ deal, fromStage, toStage, onCancel, onDone }) {
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);
  const isSample = toStage === "sample_sent";
  const [ship, setShip] = useState({
    carrier: "DHL",
    tracking_no: "",
    shipped_at: new Date().toISOString().slice(0, 10),
    notes: "",
  });

  const from = stageMeta(fromStage);
  const to = stageMeta(toStage);

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/deals/${deal.id}`, { stage: toStage, remark: remark.trim() });
      if (isSample) {
        await api.post("/shipments", {
          customer_id: deal.customer_id,
          customer_name: deal.customer_name || "",
          deal_id: deal.id,
          carrier: ship.carrier,
          tracking_no: ship.tracking_no.trim(),
          status: "pending",
          shipped_at: ship.shipped_at,
          notes: ship.notes.trim(),
        });
        toast.success(`Moved to ${to.label} · shipment created`);
      } else {
        toast.success(`Moved to ${to.label}`);
      }
      onDone?.();
    } catch (e) {
      toast.error("Failed to update stage");
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Update Lead Stage"
      subtitle={deal.title || deal.customer_name}
      onClose={onCancel}
      size="md"
    >
      <div className="flex flex-col gap-4" data-testid="stage-change-form">
        <div className="flex items-center gap-2" style={{ fontSize: 13 }}>
          <span className="tag" style={{ background: from.bg, borderColor: "var(--border)", color: from.accent }}>{from.label}</span>
          <span style={{ color: "var(--text-2)" }}>→</span>
          <span className="tag" style={{ background: to.bg, borderColor: "var(--border)", color: to.accent }}>{to.label}</span>
        </div>

        <div>
          <label className="overline" style={{ fontSize: 10 }}>Remarks (optional)</label>
          <textarea
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder="Why is this lead moving stages?"
            className="input-base"
            rows={3}
            data-testid="stage-change-remark"
            autoFocus
          />
        </div>

        {isSample && (
          <div className="card-ledger" style={{ padding: 16, background: "var(--surface-2)" }} data-testid="stage-change-shipment">
            <div className="overline" style={{ fontSize: 10, marginBottom: 10 }}>Sample Shipment</div>
            <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <div>
                <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Carrier</label>
                <select value={ship.carrier} onChange={(e) => setShip({ ...ship, carrier: e.target.value })} className="input-base" data-testid="stage-shipment-carrier">
                  {CARRIERS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Shipped Date</label>
                <input type="date" value={ship.shipped_at} onChange={(e) => setShip({ ...ship, shipped_at: e.target.value })} className="input-base font-mono-data" data-testid="stage-shipment-date" />
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Tracking #</label>
              <input value={ship.tracking_no} onChange={(e) => setShip({ ...ship, tracking_no: e.target.value })} className="input-base font-mono-data" placeholder="e.g. 1234567890" data-testid="stage-shipment-tracking" />
            </div>
            <div style={{ marginTop: 12 }}>
              <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Notes</label>
              <textarea value={ship.notes} onChange={(e) => setShip({ ...ship, notes: e.target.value })} rows={2} className="input-base" style={{ fontFamily: "inherit" }} data-testid="stage-shipment-notes" />
            </div>
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} className="btn-outline" disabled={saving}>Cancel</button>
          <button onClick={submit} className="btn-primary" disabled={saving} data-testid="stage-change-save">
            {saving ? "Saving…" : (isSample ? "Move & Create Shipment" : "Confirm Move")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
